import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type {
  EnvironmentId,
  ProjectDocumentFile,
  ProjectDocumentsStatusResult,
  ProjectOutputFile,
  ProjectOutputStatusResult,
} from "@t3tools/contracts";
import {
  createColumnHelper,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table";
import * as Schema from "effect/Schema";
import {
  EllipsisIcon,
  FolderInputIcon,
  LoaderIcon,
  RefreshCwIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fileBasename, filterFilesByPath } from "./DocumentsSidebar.logic";
import { FilesTable, type FilesTableColumnLayout } from "./documents/FilesTable";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { Sheet, SheetPopup } from "./ui/sheet";
import { cn } from "~/lib/utils";
import { readEnvironmentApi } from "~/environmentApi";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { i18n } from "~/i18n";
import { resolveEnvironmentHttpUrl } from "~/environments/runtime";
import { stackedThreadToast, toastManager } from "./ui/toast";

const SIDEBAR_WIDTH_STORAGE_KEY = "t3code:documents-panel-width:v2";
const SORTING_STORAGE_KEY = "t3code:documents-panel-sorting:v1";
const RESULTS_SORTING_STORAGE_KEY = "t3code:documents-panel-results-sorting:v1";
const TAB_STORAGE_KEY = "t3code:documents-panel-tab:v1";

const DEFAULT_SORTING: SortingState = [{ id: "status", desc: false }];
const DEFAULT_RESULTS_SORTING: SortingState = [{ id: "modified", desc: true }];

const SortingStateSchema = Schema.mutable(
  Schema.Array(Schema.Struct({ id: Schema.String, desc: Schema.Boolean })),
);
const PanelTabSchema = Schema.Literals(["materials", "results"]);
type PanelTab = typeof PanelTabSchema.Type;

const SIDEBAR_MIN_WIDTH = 320;
const SIDEBAR_DEFAULT_WIDTH = 600;

function clampSidebarWidth(width: number): number {
  const safeWidth = Number.isFinite(width) ? width : SIDEBAR_DEFAULT_WIDTH;
  const maxWidth =
    typeof window === "undefined"
      ? SIDEBAR_DEFAULT_WIDTH
      : Math.max(SIDEBAR_MIN_WIDTH, Math.round(window.innerWidth * 0.6));
  return Math.min(Math.max(safeWidth, SIDEBAR_MIN_WIDTH), maxWidth);
}

function readStoredSidebarWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
  const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
  return clampSidebarWidth(Number.isNaN(parsed) ? SIDEBAR_DEFAULT_WIDTH : parsed);
}

function encodeRoutePath(routePath: string): string {
  return routePath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

function formatDocumentSize(size: number | null): string | null {
  if (size === null) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatModifiedAt(mtimeMs: number | null): string | null {
  if (mtimeMs === null) return null;
  return new Date(mtimeMs)
    .toLocaleString(i18n.locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(/\s*г\./, "");
}

type DocumentRow = ProjectDocumentFile & { copies?: DocumentRow[] };

function buildDocumentRows(files: ProjectDocumentFile[]): DocumentRow[] {
  const byHash = new Map<string, ProjectDocumentFile[]>();
  const rows: DocumentRow[] = [];
  for (const file of files) {
    if (!file.sha256) {
      rows.push(file);
      continue;
    }
    const bucket = byHash.get(file.sha256);
    if (bucket) {
      bucket.push(file);
    } else {
      byHash.set(file.sha256, [file]);
    }
  }
  for (const bucket of byHash.values()) {
    if (bucket.length === 1) {
      rows.push(bucket[0]!);
      continue;
    }
    const ordered = bucket
      .slice()
      .sort((a, b) => a.path.length - b.path.length || a.path.localeCompare(b.path));
    const [original, ...copies] = ordered;
    rows.push({ ...original!, copies });
  }
  return rows;
}

const COLUMN_LAYOUT: Record<string, FilesTableColumnLayout> = {
  status: { width: "6rem", headerClassName: "w-24" },
  pages: { width: "4rem", headerClassName: "w-16" },
  size: { width: "5rem", headerClassName: "w-20" },
  modified: { width: "9.5rem", headerClassName: "w-[9.5rem]" },
};

type DisplayStatus = "ready" | "pending" | "error" | "unsupported";

const DISPLAY_STATUS_RANK: Record<DisplayStatus, number> = {
  ready: 3,
  pending: 2,
  error: 1,
  unsupported: 0,
};

function displayStatus(file: ProjectDocumentFile): DisplayStatus {
  if (file.state === "ok") return "ready";
  if (file.state === "failed") return "error";
  if (file.state === "unsupported") return "unsupported";
  return "pending";
}

const STATUS_BADGE_CLASS: Record<DisplayStatus, string> = {
  ready: "bg-emerald-500/10 text-emerald-500",
  pending: "bg-muted/60 text-muted-foreground",
  error: "bg-red-500/10 text-red-500",
  unsupported: "bg-muted/40 text-muted-foreground/60",
};

interface DocumentsPanelProps {
  open: boolean;
  environmentId: EnvironmentId;
  projectCwd: string | undefined;
  onClose: () => void;
}

const documentColumnHelper = createColumnHelper<DocumentRow>();
const resultColumnHelper = createColumnHelper<ProjectOutputFile>();

interface TabSwitchProps {
  tab: PanelTab;
  activeTab: PanelTab;
  label: React.ReactNode;
  onSelect: (tab: PanelTab) => void;
}

function TabSwitch({ tab, activeTab, label, onSelect }: TabSwitchProps) {
  const isActive = tab === activeTab;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => onSelect(tab)}
      className={cn(
        "-mb-px flex h-full items-center border-b-2 px-0.5 text-[12px] font-medium whitespace-nowrap transition-colors outline-none focus-visible:border-ring",
        isActive
          ? "border-amber-500 text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground/80",
      )}
    >
      {label}
    </button>
  );
}

const DocumentsPanel = memo(function DocumentsPanel({
  open,
  environmentId,
  projectCwd,
  onClose,
}: DocumentsPanelProps) {
  const { t } = useLingui();
  const [status, setStatus] = useState<ProjectDocumentsStatusResult | null>(null);
  const [outputStatus, setOutputStatus] = useState<ProjectOutputStatusResult | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
  const sidebarWidthRef = useRef(sidebarWidth);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const resizeStateRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [outputLoadError, setOutputLoadError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [activeTab, setActiveTab] = useLocalStorage(
    TAB_STORAGE_KEY,
    "materials" as PanelTab,
    PanelTabSchema,
  );
  const [sorting, setSorting] = useLocalStorage(
    SORTING_STORAGE_KEY,
    DEFAULT_SORTING,
    SortingStateSchema,
  );
  const [resultsSorting, setResultsSorting] = useLocalStorage(
    RESULTS_SORTING_STORAGE_KEY,
    DEFAULT_RESULTS_SORTING,
    SortingStateSchema,
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [resultRowSelection, setResultRowSelection] = useState<RowSelectionState>({});
  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDeferredValue(searchInput);

  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setRowSelection({});
    setResultRowSelection({});
  }, []);

  const selectTab = useCallback(
    (tab: PanelTab) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
      exitSelectionMode();
    },
    [activeTab, exitSelectionMode, setActiveTab],
  );

  const handleResizePointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: sidebarWidthRef.current,
    };
  }, []);

  const handleResizePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;
    event.preventDefault();
    const nextWidth = clampSidebarWidth(
      resizeState.startWidth + (resizeState.startX - event.clientX),
    );
    if (nextWidth === sidebarWidthRef.current) return;
    sidebarWidthRef.current = nextWidth;
    if (popupRef.current !== null) popupRef.current.style.width = `${nextWidth}px`;
  }, []);

  const handleResizePointerEnd = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId) return;
    resizeStateRef.current = null;
    setSidebarWidth(sidebarWidthRef.current);
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidthRef.current));
  }, []);

  const refresh = useCallback(() => {
    const api = readEnvironmentApi(environmentId);
    if (!api || !projectCwd) return;
    setIsLoading(true);
    setLoadError(null);
    setOutputLoadError(null);
    const errorMessage = (error: unknown) =>
      error instanceof Error ? error.message : t`An error occurred.`;
    void Promise.allSettled([
      api.projects.documentsStatus({ cwd: projectCwd }),
      api.projects.outputStatus({ cwd: projectCwd }),
    ]).then(([documents, output]) => {
      if (documents.status === "fulfilled") {
        setStatus(documents.value);
      } else {
        setLoadError(errorMessage(documents.reason));
      }
      if (output.status === "fulfilled") {
        setOutputStatus(output.value);
      } else {
        setOutputLoadError(errorMessage(output.reason));
      }
      setIsLoading(false);
    });
  }, [environmentId, projectCwd, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hostedUrl = useCallback(
    (artifactsPrefix: string | null | undefined, routePath: string | null) => {
      if (!artifactsPrefix || !routePath) return null;
      return resolveEnvironmentHttpUrl({
        environmentId,
        pathname: `/artifacts/${encodeRoutePath(`${artifactsPrefix}/${routePath}`)}`,
      });
    },
    [environmentId],
  );

  const documentUrl = useCallback(
    (routePath: string | null) => hostedUrl(status?.artifactsPrefix, routePath),
    [hostedUrl, status?.artifactsPrefix],
  );

  const resultUrl = useCallback(
    (routePath: string | null) => hostedUrl(outputStatus?.artifactsPrefix, routePath),
    [hostedUrl, outputStatus?.artifactsPrefix],
  );

  const openUrl = useCallback((url: string | null) => {
    if (url) window.open(url, "_blank", "noopener");
  }, []);

  const finishMutation = useCallback(() => {
    setRowSelection({});
    setResultRowSelection({});
    setIsMutating(false);
    refresh();
  }, [refresh]);

  const deleteFiles = useCallback(
    (files: ProjectDocumentFile[]) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !projectCwd || files.length === 0) return;
      const confirmation =
        files.length === 1
          ? t`Delete "${files[0]?.path}" from materials? The agent will no longer see it.`
          : t`Delete ${files.length} files from materials? The agent will no longer see them.`;
      if (!window.confirm(confirmation)) return;
      setIsMutating(true);
      void (async () => {
        const failures: string[] = [];
        for (const [index, file] of files.entries()) {
          try {
            await api.projects.deleteDocument({
              cwd: projectCwd,
              relativePath: file.path,
              reconcile: index === files.length - 1,
            });
          } catch {
            failures.push(file.path);
          }
        }
        if (failures.length === 0) {
          toastManager.add({
            type: "success",
            title: files.length === 1 ? t`Document deleted` : t`${files.length} documents deleted`,
          });
        } else {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t`Could not delete ${failures.length} of ${files.length} documents`,
              description: failures.slice(0, 3).join(", "),
            }),
          );
        }
        finishMutation();
      })();
    },
    [environmentId, finishMutation, projectCwd, t],
  );

  const deleteResults = useCallback(
    (files: ProjectOutputFile[]) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !projectCwd || files.length === 0) return;
      const confirmation =
        files.length === 1
          ? t`Delete "${files[0]?.path}"? This cannot be undone.`
          : t`Delete ${files.length} files? This cannot be undone.`;
      if (!window.confirm(confirmation)) return;
      setIsMutating(true);
      void (async () => {
        const failures: string[] = [];
        for (const file of files) {
          try {
            await api.projects.deleteOutputFile({ cwd: projectCwd, relativePath: file.path });
          } catch {
            failures.push(file.path);
          }
        }
        if (failures.length === 0) {
          toastManager.add({
            type: "success",
            title: files.length === 1 ? t`File deleted` : t`${files.length} files deleted`,
          });
        } else {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t`Could not delete ${failures.length} of ${files.length} files`,
              description: failures.slice(0, 3).join(", "),
            }),
          );
        }
        finishMutation();
      })();
    },
    [environmentId, finishMutation, projectCwd, t],
  );

  const addToMaterials = useCallback(
    (files: ProjectOutputFile[]) => {
      const api = readEnvironmentApi(environmentId);
      if (!api || !projectCwd || files.length === 0) return;
      setIsMutating(true);
      void (async () => {
        const failures: string[] = [];
        for (const [index, file] of files.entries()) {
          try {
            await api.projects.copyOutputToAttachments({
              cwd: projectCwd,
              relativePath: file.path,
              reconcile: index === files.length - 1,
            });
          } catch {
            failures.push(file.path);
          }
        }
        if (failures.length === 0) {
          toastManager.add({
            type: "success",
            title:
              files.length === 1
                ? t`Added to materials: ${fileBasename(files[0]!.path)}`
                : t`${files.length} files added to materials`,
          });
        } else {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: t`Could not add ${failures.length} of ${files.length} files to materials`,
              description: failures.slice(0, 3).join(", "),
            }),
          );
        }
        finishMutation();
      })();
    },
    [environmentId, finishMutation, projectCwd, t],
  );

  const files = useMemo(
    () =>
      filterFilesByPath(
        (status?.files ?? []).filter((file) => file.state !== "orphaned"),
        searchQuery,
      ),
    [status?.files, searchQuery],
  );

  const resultFiles = useMemo(
    () => filterFilesByPath(outputStatus?.files ?? [], searchQuery),
    [outputStatus?.files, searchQuery],
  );

  const rows = useMemo(() => buildDocumentRows(files), [files]);

  const statusLabels: Record<DisplayStatus, string> = useMemo(
    () => ({
      ready: t`Ready`,
      pending: t`Pending`,
      error: t`Error`,
      unsupported: t`Unsupported`,
    }),
    [t],
  );

  const selectLabels = useMemo(() => ({ all: t`Select all files`, one: t`Select file` }), [t]);

  const columns = useMemo(
    () => [
      documentColumnHelper.accessor((file) => fileBasename(file.path), {
        id: "name",
        header: () => <Trans>Name</Trans>,
        cell: ({ row, getValue }) => (
          <span
            className={cn("flex min-w-0 items-center gap-1.5", row.depth > 0 && "pl-4")}
            title={row.original.path}
          >
            <span className={cn("truncate text-[12px]", row.depth > 0 && "text-muted-foreground")}>
              {getValue()}
            </span>
            {row.depth > 0 ? (
              <Badge
                variant="secondary"
                className="shrink-0 rounded-md bg-muted/60 px-1.5 py-0 text-[10px] text-muted-foreground/70"
              >
                <Trans>copy</Trans>
              </Badge>
            ) : null}
          </span>
        ),
      }),
      documentColumnHelper.accessor((file) => DISPLAY_STATUS_RANK[displayStatus(file)], {
        id: "status",
        header: () => <Trans>Status</Trans>,
        sortDescFirst: true,
        cell: ({ row }) => {
          const display = displayStatus(row.original);
          return (
            <Badge
              variant="secondary"
              className={cn(
                "rounded-md px-1.5 py-0 text-[10px] font-medium",
                STATUS_BADGE_CLASS[display],
              )}
              title={display === "error" ? (row.original.error ?? undefined) : undefined}
            >
              {statusLabels[display]}
            </Badge>
          );
        },
      }),
      documentColumnHelper.accessor((file) => file.pageCount ?? -1, {
        id: "pages",
        header: () => <Trans>Pages</Trans>,
        cell: ({ row }) => (
          <span className="block text-right text-[12px] tabular-nums text-muted-foreground">
            {row.original.pageCount ?? "—"}
          </span>
        ),
      }),
      documentColumnHelper.accessor((file) => file.size ?? -1, {
        id: "size",
        header: () => <Trans>Size</Trans>,
        cell: ({ row }) => (
          <span className="block text-right text-[12px] tabular-nums text-muted-foreground">
            {formatDocumentSize(row.original.size) ?? "—"}
          </span>
        ),
      }),
      documentColumnHelper.accessor((file) => file.mtimeMs ?? 0, {
        id: "modified",
        header: () => <Trans>Modified</Trans>,
        sortDescFirst: true,
        cell: ({ row }) => (
          <span className="flex items-center pr-6 text-[12px] whitespace-nowrap text-muted-foreground">
            {formatModifiedAt(row.original.mtimeMs) ?? "—"}
            <span className="absolute top-1/2 right-1 -translate-y-1/2">
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      className="text-muted-foreground/40 opacity-0 group-hover/row:opacity-100 aria-expanded:opacity-100 hover:text-foreground/70"
                      aria-label={t`Document actions`}
                    />
                  }
                >
                  <EllipsisIcon className="size-3.5" />
                </MenuTrigger>
                <MenuPopup align="end">
                  <MenuItem
                    onClick={() => openUrl(documentUrl(row.original.path))}
                    disabled={documentUrl(row.original.path) === null}
                  >
                    <Trans>Download original</Trans>
                  </MenuItem>
                  {row.original.resultPath !== null ? (
                    <MenuItem
                      onClick={() => openUrl(documentUrl(row.original.resultPath))}
                      disabled={documentUrl(row.original.resultPath) === null}
                    >
                      <Trans>Download parsed JSON</Trans>
                    </MenuItem>
                  ) : null}
                  <MenuItem onClick={() => deleteFiles([row.original])} disabled={isMutating}>
                    <Trans>Delete from materials</Trans>
                  </MenuItem>
                </MenuPopup>
              </Menu>
            </span>
          </span>
        ),
      }),
    ],
    [deleteFiles, documentUrl, isMutating, openUrl, statusLabels, t],
  );

  const resultColumns = useMemo(
    () => [
      resultColumnHelper.accessor((file) => fileBasename(file.path), {
        id: "name",
        header: () => <Trans>Name</Trans>,
        cell: ({ row, getValue }) => (
          <span className="flex min-w-0 items-center gap-1.5" title={row.original.path}>
            <span className="truncate text-[12px]">{getValue()}</span>
          </span>
        ),
      }),
      resultColumnHelper.accessor((file) => file.size, {
        id: "size",
        header: () => <Trans>Size</Trans>,
        cell: ({ row }) => (
          <span className="block text-right text-[12px] tabular-nums text-muted-foreground">
            {formatDocumentSize(row.original.size) ?? "—"}
          </span>
        ),
      }),
      resultColumnHelper.accessor((file) => file.mtimeMs ?? 0, {
        id: "modified",
        header: () => <Trans>Modified</Trans>,
        sortDescFirst: true,
        cell: ({ row }) => (
          <span className="flex items-center pr-6 text-[12px] whitespace-nowrap text-muted-foreground">
            {formatModifiedAt(row.original.mtimeMs) ?? "—"}
            <span className="absolute top-1/2 right-1 -translate-y-1/2">
              <Menu>
                <MenuTrigger
                  render={
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      className="text-muted-foreground/40 opacity-0 group-hover/row:opacity-100 aria-expanded:opacity-100 hover:text-foreground/70"
                      aria-label={t`File actions`}
                    />
                  }
                >
                  <EllipsisIcon className="size-3.5" />
                </MenuTrigger>
                <MenuPopup align="end">
                  <MenuItem
                    onClick={() => openUrl(resultUrl(row.original.path))}
                    disabled={resultUrl(row.original.path) === null}
                  >
                    <Trans>Download file</Trans>
                  </MenuItem>
                  <MenuItem onClick={() => addToMaterials([row.original])} disabled={isMutating}>
                    <Trans>Add to materials</Trans>
                  </MenuItem>
                  <MenuItem onClick={() => deleteResults([row.original])} disabled={isMutating}>
                    <Trans>Delete</Trans>
                  </MenuItem>
                </MenuPopup>
              </Menu>
            </span>
          </span>
        ),
      }),
    ],
    [addToMaterials, deleteResults, isMutating, openUrl, resultUrl, t],
  );

  const isMaterialsTab = activeTab === "materials";
  const selectedDocuments = useMemo(
    () => files.filter((file) => rowSelection[file.path] === true),
    [files, rowSelection],
  );
  const selectedResults = useMemo(
    () => resultFiles.filter((file) => resultRowSelection[file.path] === true),
    [resultFiles, resultRowSelection],
  );
  const selectedCount = isMaterialsTab ? selectedDocuments.length : selectedResults.length;
  const activeStatus = isMaterialsTab ? status : outputStatus;
  const activeLoadError = isMaterialsTab ? loadError : outputLoadError;
  const activeFileCount = isMaterialsTab ? files.length : resultFiles.length;

  const materialsSummary = useMemo(() => {
    if (!status) return null;
    const total = status.files.filter((file) => file.state !== "orphaned").length;
    if (total === 0) return null;
    const pending = status.counts.unparsed + status.counts.stale;
    const failed = status.counts.failed;
    const pages = status.usage.pages;
    const left = [plural(total, { one: "# document", other: "# documents" })];
    if (pages > 0) {
      left.push(plural(pages, { one: "# page", other: "# pages" }));
    }
    const right = [t`${pending} pending`];
    if (failed > 0) {
      right.push(plural(failed, { one: "# error", other: "# errors" }));
    }
    return { left: left.join(" · "), right: right.join(" · ") };
  }, [status, t]);

  const resultsSummary = useMemo(() => {
    if (!outputStatus || outputStatus.files.length === 0) return null;
    const total = outputStatus.files.length;
    return { left: t`Total ${total}`, right: null };
  }, [outputStatus, t]);

  const activeSummary = isMaterialsTab ? materialsSummary : resultsSummary;

  const scrollResetKey = `${environmentId}\u0000${projectCwd ?? ""}\u0000${searchQuery}`;

  const placeholder: ReactNode = !projectCwd ? (
    <p className="px-4 py-10 text-center text-[12px] text-muted-foreground/40">
      <Trans>Documents are unavailable until this thread has an active project.</Trans>
    </p>
  ) : activeLoadError ? (
    <p className="px-4 py-10 text-center text-[12px] text-red-400/80">{activeLoadError}</p>
  ) : activeStatus === null ? (
    <div className="flex items-center justify-center py-12 text-muted-foreground/40">
      <LoaderIcon className="size-4 animate-spin" />
    </div>
  ) : activeFileCount === 0 ? (
    searchQuery.trim().length > 0 ? (
      <p className="px-4 py-10 text-center text-[12px] text-muted-foreground/40">
        <Trans>No documents match “{searchQuery.trim()}”.</Trans>
      </p>
    ) : isMaterialsTab ? (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-[12px] text-muted-foreground/40">
          <Trans>No materials in this project yet.</Trans>
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/30">
          <Trans>Attach files in the chat to add them.</Trans>
        </p>
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-[12px] text-muted-foreground/40">
          <Trans>No created files in this project yet.</Trans>
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/30">
          <Trans>Files the agent creates will appear here.</Trans>
        </p>
      </div>
    )
  ) : undefined;

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetPopup
        ref={popupRef}
        side="right"
        showCloseButton={false}
        keepMounted
        className="min-w-80 max-w-none p-0"
        style={{ width: sidebarWidth }}
      >
        <div className="relative flex h-full min-h-0 w-full flex-col">
          <div
            className="absolute inset-y-0 left-0 z-30 w-1 cursor-col-resize touch-none transition-colors hover:bg-primary/20 active:bg-primary/30"
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerEnd}
            onPointerCancel={handleResizePointerEnd}
            aria-hidden="true"
          />
          {/* Header */}
          <div className="flex h-12 shrink-0 items-stretch justify-between border-b border-border/60 px-3">
            <div role="tablist" className="flex min-w-0 items-stretch gap-4">
              <TabSwitch
                tab="materials"
                activeTab={activeTab}
                label={<Trans>Materials</Trans>}
                onSelect={selectTab}
              />
              <TabSwitch
                tab="results"
                activeTab={activeTab}
                label={<Trans>Created</Trans>}
                onSelect={selectTab}
              />
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={refresh}
                disabled={isLoading || !projectCwd}
                aria-label={t`Refresh documents`}
              >
                <RefreshCwIcon className={cn("size-4", isLoading && "animate-spin")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0"
                onClick={onClose}
                aria-label={t`Close documents panel`}
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b border-border/60 bg-muted/30 px-3">
            <Input
              size="sm"
              type="search"
              placeholder={t`Search documents`}
              value={searchInput}
              onValueChange={(value) => setSearchInput(value)}
              className="max-w-56 flex-1 text-sm sm:text-xs"
              disabled={!projectCwd}
            />
            <div className="flex shrink-0 items-center gap-2">
              {isSelectionMode ? (
                <>
                  {selectedCount > 0 ? (
                    <span className="text-[12px] whitespace-nowrap text-muted-foreground">
                      {t`${selectedCount} selected`}
                    </span>
                  ) : null}
                  {isMaterialsTab ? null : (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => addToMaterials(selectedResults)}
                      disabled={isMutating || selectedResults.length === 0}
                    >
                      {isMutating ? (
                        <LoaderIcon className="size-3 animate-spin" />
                      ) : (
                        <FolderInputIcon className="size-3" />
                      )}
                      <Trans>Add to materials</Trans>
                    </Button>
                  )}
                  <Button
                    size="xs"
                    variant="destructive"
                    onClick={() =>
                      isMaterialsTab
                        ? deleteFiles(selectedDocuments)
                        : deleteResults(selectedResults)
                    }
                    disabled={isMutating || selectedCount === 0}
                  >
                    {isMutating ? (
                      <LoaderIcon className="size-3 animate-spin" />
                    ) : (
                      <Trash2Icon className="size-3" />
                    )}
                    <Trans>Delete</Trans>
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={exitSelectionMode}
                    disabled={isMutating}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                </>
              ) : (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setIsSelectionMode(true)}
                  disabled={activeFileCount === 0}
                >
                  <Trans>Select</Trans>
                </Button>
              )}
            </div>
          </div>

          {isMaterialsTab ? (
            <FilesTable
              data={rows}
              columns={columns}
              layout={COLUMN_LAYOUT}
              getRowId={(row) => row.path}
              getSubRows={(row) => row.copies}
              sorting={sorting}
              onSortingChange={setSorting}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              selectionMode={isSelectionMode}
              selectLabels={selectLabels}
              virtualizerEnabled={open}
              scrollResetKey={scrollResetKey}
              placeholder={placeholder}
            />
          ) : (
            <FilesTable
              data={resultFiles}
              columns={resultColumns}
              layout={COLUMN_LAYOUT}
              getRowId={(row) => row.path}
              sorting={resultsSorting}
              onSortingChange={setResultsSorting}
              rowSelection={resultRowSelection}
              onRowSelectionChange={setResultRowSelection}
              selectionMode={isSelectionMode}
              selectLabels={selectLabels}
              virtualizerEnabled={open}
              scrollResetKey={scrollResetKey}
              placeholder={placeholder}
            />
          )}

          {/* Status bar */}
          {projectCwd && activeLoadError === null && activeSummary !== null ? (
            <div className="flex h-7 shrink-0 items-center justify-between gap-3 border-t border-border/60 px-3 text-[11px] text-muted-foreground">
              <span className="truncate">{activeSummary.left}</span>
              {activeSummary.right !== null ? (
                <span className="shrink-0">{activeSummary.right}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      </SheetPopup>
    </Sheet>
  );
});

export default DocumentsPanel;
export type { DocumentsPanelProps };
